import { LoaderError } from './runtime/containment.js';
import type {
  HostSlotContext,
  HostSlotFillRequest,
  HostSlotHandle,
  HostSlotLoaderPort,
} from './runtime/hostSlotPort.js';
import { ActivityStatus } from './runtime/status.js';
import type { Telemetry } from './runtime/telemetry.js';

/**
 * The handle `HostSlot` gets back from a synchronous `fill`, while the runtime
 * chunk is still loading. It keeps the latest `update`, forwards everything once
 * the runtime is there, and drops the placement if it is unmounted first.
 */
export class LazyHandle implements HostSlotHandle {
  readonly ready: Promise<void>;
  readonly released: Promise<void>;
  /** Loading until the runtime attaches, then whatever the placement reports. */
  readonly status = new ActivityStatus('loading');

  private resolveReady!: () => void;
  private rejectReady!: (error: unknown) => void;
  private resolveReleased!: () => void;
  private inner: HostSlotHandle | undefined;
  private pending: HostSlotContext | undefined;
  private cancelled = false;

  constructor(
    private readonly request: HostSlotFillRequest,
    loadRuntime: () => Promise<HostSlotLoaderPort>,
    private readonly telemetry: Telemetry,
    private readonly tenantSlug: string,
  ) {
    this.ready = new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
    this.ready.catch(() => undefined);
    this.released = new Promise<void>((resolve) => {
      this.resolveReleased = resolve;
    });

    loadRuntime().then(
      (runtime) => this.attach(runtime),
      (error: unknown) => this.fail(error),
    );
  }

  update(context: HostSlotContext): void {
    if (this.inner) {
      this.inner.update(context);
      return;
    }
    this.pending = context;
  }

  unmount(): void {
    this.cancelled = true;
    this.inner?.unmount();
  }

  private attach(runtime: HostSlotLoaderPort): void {
    if (this.cancelled) {
      return;
    }
    let handle: HostSlotHandle;
    try {
      handle = runtime.fill({
        ...this.request,
        context: this.pending ?? this.request.context,
      });
    } catch (error) {
      this.fail(error);
      return;
    }
    this.inner = handle;
    this.followStatus(handle);
    handle.ready.then(this.resolveReady, this.rejectReady);
    handle.released?.then(this.resolveReleased, () => undefined);
  }

  private followStatus(handle: HostSlotHandle): void {
    const inner = handle.status;
    if (!inner) {
      return;
    }
    try {
      this.status.set(inner.read());
      inner.subscribe(() => this.status.set(inner.read()));
    } catch (error) {
      this.telemetry.error(error, {
        slot: this.request.slot,
        reason: 'attribution-failed',
      });
    }
  }

  private fail(error: unknown): void {
    if (this.cancelled) {
      return;
    }
    this.telemetry.error(error, {
      slot: this.request.slot,
      reason: 'load-failed',
    });
    this.telemetry.outcome({
      outcome: 'fallback',
      reason: 'load-failed',
      slot: this.request.slot,
      tenantSlug: this.tenantSlug,
    });
    this.rejectReady(
      new LoaderError('load-failed', 'The loader code failed to load', {
        cause: error,
      }),
    );
  }
}
